import { useState } from 'react';
import { Heart, Copy, Share2, Download, Twitter, Send, Check } from 'lucide-react';
import { useToggleFavorite } from '@/hooks/useFavorites';
import { useShare } from '@/hooks/useShare';
import { copyAsPlainText, copyAsMarkdown, copyAsHTML } from '@/utils/clipboard';
import { Modal } from '@/components/ui/Modal';

interface QuoteActionsProps {
  quoteId: number;
  text: string;
  author: string;
  isFavorited?: boolean;
  downloadRef?: React.RefObject<HTMLElement>;
  onDownload?: () => void;
  layout?: 'horizontal' | 'vertical';
}

export function QuoteActions({
  quoteId,
  text,
  author,
  isFavorited = false,
  downloadRef,
  onDownload,
  layout = 'horizontal',
}: QuoteActionsProps) {
  const toggleFavorite = useToggleFavorite();
  const { shareTwitter, shareTelegram } = useShare();
  const [copied, setCopied] = useState(false);
  const [showCopyMenu, setShowCopyMenu] = useState(false);

  const shareText = `"${text}" — ${author || 'Unknown'}`;

  const handleCopy = async (format: 'plain' | 'markdown' | 'html') => {
    let success = false;
    switch (format) {
      case 'plain':
        success = await copyAsPlainText(text, author);
        break;
      case 'markdown':
        success = await copyAsMarkdown(text, author);
        break;
      case 'html':
        success = await copyAsHTML(text, author);
        break;
    }
    if (success) {
      setCopied(true);
      setShowCopyMenu(false);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const btnClass =
    layout === 'vertical'
      ? 'flex items-center gap-3 w-full px-4 py-3 rounded-xl hover:bg-surface-100 transition-colors text-left text-sm'
      : 'p-2.5 rounded-full hover:bg-surface-100 transition-colors';

  return (
    <>
      <div className={layout === 'vertical' ? 'flex flex-col gap-1' : 'flex items-center gap-1'}>
        <button onClick={() => toggleFavorite.mutate({ quoteId, isFavorited })} className={btnClass}>
          <Heart size={18} className={isFavorited ? 'fill-red-500 text-red-500' : 'text-surface-400'} />
          {layout === 'vertical' && <span>{isFavorited ? 'Unfavorite' : 'Favorite'}</span>}
        </button>

        <button onClick={() => setShowCopyMenu(true)} className={btnClass}>
          {copied ? <Check size={18} className="text-green-500" /> : <Copy size={18} className="text-surface-400" />}
          {layout === 'vertical' && <span>{copied ? 'Copied!' : 'Copy'}</span>}
        </button>

        <button onClick={() => shareTwitter(shareText)} className={btnClass}>
          <Twitter size={18} className="text-surface-400" />
          {layout === 'vertical' && <span>Twitter</span>}
        </button>

        <button onClick={() => shareTelegram(shareText)} className={btnClass}>
          <Send size={18} className="text-surface-400" />
          {layout === 'vertical' && <span>Telegram</span>}
        </button>

        {onDownload && (
          <button onClick={onDownload} className={btnClass}>
            <Download size={18} className="text-surface-400" />
            {layout === 'vertical' && <span>Download</span>}
          </button>
        )}

        {downloadRef && (
          <button onClick={() => downloadRef.current?.scrollIntoView({ behavior: 'smooth' })} className={btnClass}>
            <Share2 size={18} className="text-surface-400" />
            {layout === 'vertical' && <span>Share</span>}
          </button>
        )}
      </div>

      <Modal isOpen={showCopyMenu} onClose={() => setShowCopyMenu(false)} title="Copy Quote">
        <div className="flex flex-col gap-2">
          <button
            onClick={() => handleCopy('plain')}
            className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-surface-100 transition-colors text-left"
          >
            <Copy size={18} className="text-surface-400" />
            <div>
              <p className="font-medium">Plain Text</p>
              <p className="text-sm text-surface-500">"Quote" — Author</p>
            </div>
          </button>
          <button
            onClick={() => handleCopy('markdown')}
            className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-surface-100 transition-colors text-left"
          >
            <Copy size={18} className="text-surface-400" />
            <div>
              <p className="font-medium">Markdown</p>
              <p className="text-sm text-surface-500">{'> "Quote"'}</p>
            </div>
          </button>
          <button
            onClick={() => handleCopy('html')}
            className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-surface-100 transition-colors text-left"
          >
            <Copy size={18} className="text-surface-400" />
            <div>
              <p className="font-medium">HTML</p>
              <p className="text-sm text-surface-500">&lt;blockquote&gt;</p>
            </div>
          </button>
        </div>
      </Modal>
    </>
  );
}
